// frontend/src/pages/Profile.js

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Container, Row, Col, Modal, ListGroup, Form, Card } from 'react-bootstrap';
import { AuthService, ProfileService, RechargeService, WithdrawalService } from "../services";
import UserService from "../services/user.service";
import AppHeader from '../components/AppHeader';
import './Profile.css';

const Profile = (props) => {
  // --- STATE MANAGEMENT ---
  const [currentView, setCurrentView] = useState('profile');
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [newSignature, setNewSignature] = useState('');

  // Recharge/Withdrawal states
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalChannel, setWithdrawalChannel] = useState('Channel 01');
  const [selectedWallet, setSelectedWallet] = useState('');
  const [fundPassword, setFundPassword] = useState('');

  // Wallet Data State
  const [digitalWallets, setDigitalWallets] = useState([]);
  const [bankWallets, setBankWallets] = useState([]);

  // Add/Edit Wallet Modal State
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletModalType, setWalletModalType] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentWalletId, setCurrentWalletId] = useState(null);

  // Delete Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', id: null });

  // Forms
  const [digitalForm, setDigitalForm] = useState({ currency: '', classification: '', address: '', comment: '' });
  const [bankForm, setBankForm] = useState({ holderName: '', bankName: '', accountNo: '', ifscCode: '' });

  // Crypto Data Options
  const cryptoOptions = [
    { code: 'BTC', name: 'Bitcoin', classifications: ['BTC', 'SegWit'] },
    { code: 'ETH', name: 'Ethereum', classifications: ['ERC20', 'BEP20'] },
    { code: 'USDT', name: 'Tether', classifications: ['TRC20', 'ERC20', 'BEP20'] },
    { code: 'XRP', name: 'XRP', classifications: ['XRP'] },
    { code: 'BCH', name: 'Bitcoin Cash', classifications: ['BCH'] },
    { code: 'BSV', name: 'Bitcoin SV', classifications: ['BSV'] },
    { code: 'LTC', name: 'Litecoin', classifications: ['LTC'] },
    { code: 'BNB', name: 'Binance Coin', classifications: ['BEP2', 'BEP20'] },
    { code: 'EOS', name: 'EOS', classifications: ['EOS'] },
    { code: 'XTZ', name: 'Tezos', classifications: ['XTZ'] },
  ];

  const [userData, setUserData] = useState({
    username: 'Guest',
    balance: '0.00',
    frozenBalance: '0.00',
    availableBalance: '0.00',
    frozen: '0',
    score: '100',
    avatar: 'G',
    gender: 'Male',
    signature: 'Not set',
    invitationCode: 'N/A'
  });

  // Messages State
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);

  const currentUser = AuthService.getCurrentUser();
  const currentUsername = currentUser ? currentUser.username : null;
  const isMounted = React.useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // --- FETCH SITE MESSAGES ---
  const fetchSiteMessages = useCallback(async () => {
    try {
      const res = await UserService.getUserMessages();
      const data = Array.isArray(res.data) ? res.data : (res.data.messages || res.data.data || []);
      if (isMounted.current) {
        setMessages(data);
        const unread = data.filter(m => !m.isRead).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error("Error fetching site messages:", err);
    }
  }, []);

  // --- LOAD PROFILE DATA FROM BACKEND ---
  const loadProfileData = useCallback(async () => {
    const user = AuthService.getCurrentUser();
    if (!user) return;

    try {
      const response = await ProfileService.getProfile();
      if (!isMounted.current) return;
      const data = response.data;

      const totalBalance = data.balance !== undefined ? parseFloat(data.balance) : 0;
      const frozenBalance = data.frozenBalance !== undefined ? parseFloat(data.frozenBalance) : 0;
      const availableBalance = data.availableBalance !== undefined
        ? parseFloat(data.availableBalance)
        : (totalBalance - frozenBalance);

      setUserData({
        username: data.username || user.username,
        balance: totalBalance.toFixed(2),
        frozenBalance: frozenBalance.toFixed(2),
        availableBalance: availableBalance.toFixed(2),
        frozen: data.isFrozen ? 'Frozen' : '0',
        score: data.score || data.creditScore || '100',
        avatar: (data.username || user.username).charAt(0).toUpperCase(),
        gender: data.gender || 'Male',
        signature: data.signature || 'Not set',
        invitationCode: data.invitationCode || 'N/A'
      });

      // Set Wallets from Database Response
      setDigitalWallets(data.digitalWallets || []);
      setBankWallets(data.bankWallets || []);

      // Update global balance
      window.dispatchEvent(new CustomEvent('balanceUpdate', {
        detail: { newBalance: data.balance }
      }));

    } catch (error) {
      console.error("Error loading profile:", error);
      if (isMounted.current && error.response && error.response.status === 401) {
        AuthService.logout();
        window.location.reload();
      }
    }
  }, []);

  // --- FETCH DATA ON MOUNT ---
  // Depend on the username string. getCurrentUser() returns a new object
  // every render, and using that object as a dependency refetches forever.
  // props.history is only the logged-out redirect and must not retrigger this.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentUsername) {
      props.history.push("/login");
      return;
    }
    loadProfileData();
    fetchSiteMessages();
  }, [currentUsername, loadProfileData, fetchSiteMessages]);

  // --- RE-FETCH MESSAGES WHEN ENTERING SITE MESSAGES VIEW ---
  useEffect(() => {
    if (currentView === 'siteMessages') {
      fetchSiteMessages();
    }
  }, [currentView, fetchSiteMessages]);

  // --- MARK MESSAGE AS READ ---
  const handleOpenMessage = async (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);

    if (!message.isRead) {
      try {
        await UserService.markMessageAsRead(message._id);
        // Update local state
        setMessages(prev => prev.map(m => m._id === message._id ? { ...m, isRead: true } : m));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error("Error marking message as read:", err);
      }
    }
  };

  const handleLogout = () => {
    AuthService.logout();
    props.history.push("/login");
  };

  // --- RECHARGE HANDLER ---
  const handleRecharge = async () => {
    if (!rechargeAmount || rechargeAmount <= 0) {
      alert("Please enter a valid recharge amount");
      return;
    }
    try {
      await RechargeService.createRecharge(parseFloat(rechargeAmount));
      if (!isMounted.current) return;
      alert("Recharge request submitted successfully! Please wait for admin approval.");
      setShowRechargeModal(false);
      setRechargeAmount('');
      loadProfileData();
    } catch (error) {
      if (!isMounted.current) return;
      alert("Failed to submit recharge request: " + (error.response?.data?.message || error.message));
    }
  };

  // --- WITHDRAWAL HANDLER ---
  const handleWithdrawal = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      alert("Please enter a valid withdrawal amount");
      return;
    }
    if (!selectedWallet) {
      alert("Please select a wallet");
      return;
    }
    if (!fundPassword) {
      alert("Please enter your fund password");
      return;
    }

    try {
      let walletData;
      try {
        walletData = JSON.parse(selectedWallet);
      } catch {
        alert("Invalid wallet selection");
        return;
      }

      const { type: walletType, id: walletId } = walletData;

      const selectedWalletObj = walletType === 'digital'
        ? digitalWallets.find(w => w._id === walletId)
        : bankWallets.find(w => w._id === walletId);

      if (!selectedWalletObj) {
        alert("Selected wallet not found");
        return;
      }

      let walletDetails = {};

      if (walletType === 'digital') {
        walletDetails = {
          currency: selectedWalletObj.currency || 'Bitcoin',
          classification: selectedWalletObj.classification || 'Cryptocurrency',
          address: selectedWalletObj.address || ''
        };
        if (!walletDetails.currency || !walletDetails.address) {
          alert("Please ensure wallet has currency and address");
          return;
        }
      } else {
        walletDetails = {
          holderName: selectedWalletObj.holderName || '',
          bankName: selectedWalletObj.bankName || '',
          accountNo: selectedWalletObj.accountNo || '',
          ifscCode: selectedWalletObj.ifscCode || ''
        };
        if (!walletDetails.accountNo || !walletDetails.ifscCode) {
          alert("Please ensure bank account has account number and IFSC code");
          return;
        }
      }

      const payload = {
        amount: parseFloat(withdrawalAmount),
        walletType: walletType,
        walletDetails: walletDetails,
        fundPassword: fundPassword,
        withdrawalChannel: withdrawalChannel || 'Channel 01'
      };

      const response = await WithdrawalService.createWithdrawal(payload);
      if (!isMounted.current) return;

      alert("Withdrawal request submitted successfully! Please wait for admin approval.");

      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setSelectedWallet('');
      setFundPassword('');
      setWithdrawalChannel('Channel 01');

      await loadProfileData();

    } catch (error) {
      if (!isMounted.current) return;

      if (error.response?.status === 400) {
        if (error.response?.data?.message === "All fields are required for withdrawal") {
          alert("Authentication error. Please check if your token is valid.");
          AuthService.logout();
          props.history.push("/login");
        } else {
          alert(error.response?.data?.message || "Validation failed");
        }
      } else {
        alert(`Error: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleGenderSelect = async (selectedGender) => {
    setUserData(prev => ({ ...prev, gender: selectedGender }));
    setShowGenderModal(false);
    try {
      await ProfileService.updateProfile({ gender: selectedGender, signature: userData.signature });
    } catch (error) {
      console.error("Failed to save gender", error);
      alert("Failed to update gender. Please try again.");
    }
  };

  const handleSignatureUpdate = async () => {
    if (!newSignature.trim()) {
      alert("Signature cannot be empty");
      return;
    }
    try {
      await ProfileService.updateProfile({ gender: userData.gender, signature: newSignature });
      if (!isMounted.current) return;
      setUserData(prev => ({ ...prev, signature: newSignature }));
      setShowSignatureModal(false);
      setNewSignature('');
    } catch (error) {
      console.error("Failed to update signature", error);
      alert("Failed to update signature");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  // --- WALLET CRUD HANDLERS ---
  const openAddWalletModal = (type) => {
    setWalletModalType(type);
    setIsEditing(false);
    setDigitalForm({ currency: '', classification: '', address: '', comment: '' });
    setBankForm({ holderName: '', bankName: '', accountNo: '', ifscCode: '' });
    setShowWalletModal(true);
  };

  const openEditWalletModal = (type, wallet) => {
    setWalletModalType(type);
    setIsEditing(true);
    setCurrentWalletId(wallet._id || wallet.id);
    if (type === 'digital') {
      setDigitalForm({ currency: wallet.currency, classification: wallet.classification, address: wallet.address, comment: wallet.comment });
    } else {
      setBankForm({ holderName: wallet.holderName, bankName: wallet.bankName, accountNo: wallet.accountNo, ifscCode: wallet.ifscCode });
    }
    setShowWalletModal(true);
  };

  const initiateDelete = (type, id) => {
    setDeleteTarget({ type, id });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await ProfileService.updateWallet({
        [deleteTarget.type === 'digital' ? 'digitalWallets' : 'bankWallets']:
          deleteTarget.type === 'digital'
            ? digitalWallets.filter(w => w._id !== deleteTarget.id)
            : bankWallets.filter(w => w._id !== deleteTarget.id)
      });
      if (!isMounted.current) return;
      loadProfileData();
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting wallet:", error);
      alert("Failed to delete wallet. Please try again.");
      if (isMounted.current) setShowDeleteModal(false);
    }
  };

  const handleWalletSubmit = async () => {
    if (walletModalType === 'digital') {
      if (!digitalForm.currency || !digitalForm.address) return alert("Please fill required fields");
    } else {
      if (!bankForm.accountNo) return alert("Account Number is required");
      if (!bankForm.ifscCode) return alert("IFSC Code is required");
      if (!bankForm.holderName || !bankForm.bankName) return alert("Please fill all fields");
    }

    try {
      const walletData = walletModalType === 'digital' ? digitalForm : bankForm;

      if (isEditing) {
        const updatedWallets = walletModalType === 'digital'
          ? digitalWallets.map(w => w._id === currentWalletId ? { ...w, ...walletData } : w)
          : bankWallets.map(w => w._id === currentWalletId ? { ...w, ...walletData } : w);

        await ProfileService.updateWallet({
          [walletModalType === 'digital' ? 'digitalWallets' : 'bankWallets']: updatedWallets
        });
      } else {
        const newWallet = { ...walletData, _id: Date.now().toString() };
        const updatedWallets = walletModalType === 'digital'
          ? [...digitalWallets, newWallet]
          : [...bankWallets, newWallet];

        await ProfileService.updateWallet({
          [walletModalType === 'digital' ? 'digitalWallets' : 'bankWallets']: updatedWallets
        });
      }

      if (!isMounted.current) return;
      alert(isEditing ? "Wallet updated successfully!" : "Wallet added successfully!");
      setDigitalForm({ currency: '', classification: '', address: '', comment: '' });
      setBankForm({ holderName: '', bankName: '', accountNo: '', ifscCode: '' });
      setIsEditing(false);
      setCurrentWalletId(null);
      await loadProfileData();
      setShowWalletModal(false);
    } catch (error) {
      console.error("Wallet operation failed:", error);
      alert("Failed to save wallet. Please try again.");
    }
  };

  // --- RENDER HELPERS ---
  const WalletRow = ({ label, value, showCopy }) => (
    <div className="d-flex align-items-center mb-2" style={{ fontSize: '14px' }}>
      <div style={{ minWidth: '100px', color: '#848e9c', fontWeight: 'bold' }}>{label}</div>
      <div className="text-truncate flex-grow-1" style={{ color: 'white', marginRight: '10px' }}>{value}</div>
      {showCopy && (
        <div style={{ color: '#f6465d', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }} onClick={() => copyToClipboard(value)}>
          Copy
        </div>
      )}
    </div>
  );

  // Menu items with notification badge on Site Messages
  const menuItems = [
    { name: "Personal Info", icon: "👤", action: () => setCurrentView('personalInfo') },
    { name: "My Wallet", icon: "💳", action: () => setCurrentView('myWallet') },
    { name: "Security Settings", icon: "🔒", action: () => setCurrentView('securitySettings') },
    { name: "Platform Wallet", icon: "📱", action: () => setCurrentView('platformWallet') },
    { name: "Site Notification", icon: "🔔", action: () => setCurrentView('siteNotification') },
    {
      name: "Site Messages",
      icon: "💬",
      action: () => { setCurrentView('siteMessages'); fetchSiteMessages(); },
      badge: unreadCount
    },
    { name: "About Company", icon: "ℹ️", action: () => setCurrentView('aboutCompany') },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString(undefined, {
      year:   'numeric',
      month:  'short',
      day:    'numeric',
      hour:   '2-digit',
      minute: '2-digit',
    });
  };

  // Use sentAt if available, fall back to createdAt
  const formatMessageDate = (item) => {
    const raw = item.sentAt || item.createdAt;
    return formatDate(raw);
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderMainProfile = () => (
    <div className="profile-container">
      <AppHeader />
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col md={12} lg={8}>
            <div className="page-header mb-3">
              <div className="header-title">Profile</div>
            </div>

            <div className="profile-info-card">
              <div className="user-details">
                {/* Avatar with notification badge */}
                <div className="avatar-wrapper" style={{ position: 'relative' }}>
                  <div className="avatar">{userData.avatar}</div>
                  {unreadCount > 0 && (
                    <span
                      onClick={() => { setCurrentView('siteMessages'); fetchSiteMessages(); }}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        backgroundColor: '#f6465d',
                        color: 'white',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        border: '2px solid #131821',
                        zIndex: 10
                      }}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                <div className="info-text">
                  <p>UserName: <span>{userData.username}</span></p>
                  <p>Total Balance: <span className="text-success fw-bold">{userData.balance}</span> <span className="copy-btn" onClick={() => copyToClipboard(userData.balance)}>Copy</span></p>
                  <p>Available Balance: <span className="text-primary fw-bold">{userData.availableBalance}</span></p>
                  <p>Frozen Balance: <span className="text-warning fw-bold">{userData.frozenBalance}</span></p>
                  <p>Status: <span className={userData.frozen === 'Frozen' ? 'text-danger fw-bold' : 'text-success'}>{userData.frozen === 'Frozen' ? '❄️ Frozen' : '✅ Active'}</span></p>
                  <p>Credit Score: <span>{userData.score}</span></p>
                </div>
              </div>
              <Row className="mt-3">
                <Col>
                  <Button
                    className="w-100 btn-recharge"
                    onClick={() => setShowRechargeModal(true)}
                    disabled={userData.frozen === 'Frozen'}
                    style={userData.frozen === 'Frozen' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    Recharge
                  </Button>
                </Col>
                <Col>
                  <Button
                    className="w-100 btn-withdraw"
                    onClick={() => setShowWithdrawalModal(true)}
                    disabled={userData.frozen === 'Frozen'}
                    style={userData.frozen === 'Frozen' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    Withdraw
                  </Button>
                </Col>
              </Row>
            </div>

            <div className="profile-menu mt-3">
              {menuItems.map((item, index) => (
                <div className="menu-item" key={index} onClick={item.action || null}>
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-name" style={{ position: 'relative' }}>
                    {item.name}
                    {item.badge > 0 && (
                      <span style={{
                        marginLeft: '8px',
                        backgroundColor: '#f6465d',
                        color: 'white',
                        borderRadius: '10px',
                        padding: '1px 7px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        verticalAlign: 'middle'
                      }}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </span>
                  <span className="menu-arrow">›</span>
                </div>
              ))}
            </div>

            <div className="logout-wrapper mt-4 mb-5">
              <Button className="w-100 btn-logout" onClick={handleLogout}>Logout</Button>
            </div>
          </Col>
        </Row>
      </Container>

      {/* RECHARGE MODAL */}
      <Modal show={showRechargeModal} onHide={() => setShowRechargeModal(false)} centered className="dark-modal">
        <Modal.Header closeButton style={{ backgroundColor: '#1e2329', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <Modal.Title>Recharge Account</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#131821', color: 'white' }}>
          <Form.Group>
            <Form.Label>Recharge Amount</Form.Label>
            <Form.Control
              type="number"
              style={{ backgroundColor: 'white', color: 'black' }}
              placeholder="Enter amount"
              value={rechargeAmount}
              onChange={(e) => setRechargeAmount(e.target.value)}
            />
          </Form.Group>
          <div className="mt-3 p-3" style={{ backgroundColor: '#2a2e39', borderRadius: '5px' }}>
            <small className="text-warning">
              💡 Please contact teacher to get the latest channels for recharging.
              Thank you for your support and trust.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#131821', borderTop: '1px solid #2a2e39' }}>
          <Button variant="secondary" onClick={() => setShowRechargeModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleRecharge}>Submit Recharge</Button>
        </Modal.Footer>
      </Modal>

      {/* WITHDRAWAL MODAL */}
      <Modal show={showWithdrawalModal} onHide={() => setShowWithdrawalModal(false)} centered className="dark-modal">
        <Modal.Header closeButton style={{ backgroundColor: '#1e2329', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <Modal.Title>Withdraw Funds</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#131821', color: 'white' }}>
          <Form.Group>
            <Form.Label>Withdrawal Amount</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter amount"
              value={withdrawalAmount}
              onChange={(e) => setWithdrawalAmount(e.target.value)}
              style={{ backgroundColor: 'white', color: 'black' }}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Select Withdrawal Channel</Form.Label>
            <Form.Control
              as="select"
              value={withdrawalChannel}
              onChange={(e) => setWithdrawalChannel(e.target.value)}
              style={{ backgroundColor: 'white', color: 'black' }}
            >
              <option value="Channel 01">Channel 01</option>
              <option value="Channel 02">Channel 02</option>
              <option value="Channel 03">Channel 03</option>
              <option value="Channel 04">Channel 04</option>
              <option value="Channel 05">Channel 05</option>
            </Form.Control>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Select Wallet</Form.Label>
            <Form.Control
              as="select"
              value={selectedWallet}
              onChange={(e) => setSelectedWallet(e.target.value)}
              style={{ backgroundColor: 'white', color: 'black' }}
            >
              <option value="">Select Wallet</option>
              {bankWallets.length > 0 && (
                <optgroup label="🏦 My Bank Accounts">
                  {bankWallets.map(wallet => (
                    <option key={wallet._id} value={JSON.stringify({ type: 'bank', id: wallet._id })}>
                      🏦 {wallet.bankName} - {wallet.accountNo}
                    </option>
                  ))}
                </optgroup>
              )}
              {digitalWallets.length > 0 && (
                <optgroup label="₿ My Digital Wallets">
                  {digitalWallets.map(wallet => (
                    <option key={wallet._id} value={JSON.stringify({ type: 'digital', id: wallet._id })}>
                      ₿ {wallet.currency} - {wallet.address ? wallet.address.substring(0, 10) : ''}...
                    </option>
                  ))}
                </optgroup>
              )}
              {bankWallets.length === 0 && digitalWallets.length === 0 && (
                <option disabled>No wallets available. Please add a wallet first.</option>
              )}
            </Form.Control>
          </Form.Group>

          {selectedWallet && (() => {
            const walletData = JSON.parse(selectedWallet);
            const walletType = walletData.type;
            const walletId = walletData.id;
            const walletDetails = walletType === 'digital'
              ? digitalWallets.find(w => w._id === walletId)
              : bankWallets.find(w => w._id === walletId);

            if (!walletDetails) return null;

            return (
              <>
                {walletType === 'bank' && (
                  <>
                    <Form.Group className="mt-3">
                      <Form.Label>Account Holder Name</Form.Label>
                      <Form.Control type="text" value={walletDetails.holderName || ''} readOnly style={{ backgroundColor: '#f0f0f0', color: 'black' }} />
                    </Form.Group>
                    <Form.Group className="mt-3">
                      <Form.Label>Bank Name</Form.Label>
                      <Form.Control type="text" value={walletDetails.bankName || ''} readOnly style={{ backgroundColor: '#f0f0f0', color: 'black' }} />
                    </Form.Group>
                    <Form.Group className="mt-3">
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control type="text" value={walletDetails.accountNo || ''} readOnly style={{ backgroundColor: '#f0f0f0', color: 'black' }} />
                    </Form.Group>
                    <Form.Group className="mt-3">
                      <Form.Label>IFSC Code</Form.Label>
                      <Form.Control type="text" value={walletDetails.ifscCode || ''} readOnly style={{ backgroundColor: '#f0f0f0', color: 'black' }} />
                    </Form.Group>
                  </>
                )}
                {walletType === 'digital' && (
                  <>
                    <Form.Group className="mt-3">
                      <Form.Label>Currency</Form.Label>
                      <Form.Control type="text" value={walletDetails.currency || ''} readOnly style={{ backgroundColor: '#f0f0f0', color: 'black' }} />
                    </Form.Group>
                    <Form.Group className="mt-3">
                      <Form.Label>Classification</Form.Label>
                      <Form.Control type="text" value={walletDetails.classification || ''} readOnly style={{ backgroundColor: '#f0f0f0', color: 'black' }} />
                    </Form.Group>
                    <Form.Group className="mt-3">
                      <Form.Label>Wallet Address</Form.Label>
                      <Form.Control type="text" value={walletDetails.address || ''} readOnly style={{ backgroundColor: '#f0f0f0', color: 'black' }} />
                    </Form.Group>
                  </>
                )}
              </>
            );
          })()}

          <Form.Group className="mt-3">
            <Form.Label>Fund Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter fund password"
              value={fundPassword}
              onChange={(e) => setFundPassword(e.target.value)}
              style={{ backgroundColor: 'white', color: 'black' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#131821', borderTop: '1px solid #2a2e39' }}>
          <Button variant="secondary" onClick={() => setShowWithdrawalModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleWithdrawal}>Submit Withdrawal</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  const renderPersonalInfo = () => (
    <div className="personal-info-wrapper" style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <span className="mr-3" style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('profile')}>❮ Back</span>
          <div className="flex-grow-1 text-center font-weight-bold" style={{ fontSize: '18px' }}>Personal Info</div>
          <div style={{ width: '60px' }}></div>
        </div>
        <div className="mt-2" style={{ borderTop: '1px solid #2a2e39' }}>
          <div className="d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: '#131821', borderBottom: '1px solid #2a2e39' }}>
            <div className="d-flex align-items-center"><span style={{ fontSize: '24px' }}>👤</span><span style={{ color: '#fff', marginLeft: '12px', fontWeight: '500', fontSize: '16px' }}>Avatar</span></div>
            <div className="d-flex align-items-center"><div className="avatar-small mr-2" style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f3ba30', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>{userData.avatar}</div><span className="text-muted">›</span></div>
          </div>
          <div className="d-flex justify-content-between align-items-center p-3" onClick={() => setShowGenderModal(true)} style={{ backgroundColor: '#131821', borderBottom: '1px solid #2a2e39', cursor: 'pointer' }}>
            <div className="d-flex align-items-center"><span style={{ fontSize: '24px' }}>⚥</span><span style={{ color: '#fff', marginLeft: '12px', fontWeight: '500', fontSize: '16px' }}>Gender</span></div>
            <div className="d-flex align-items-center"><span style={{ color: '#848e9c', marginRight: '10px' }}>{userData.gender}</span><span className="text-muted">›</span></div>
          </div>
          <div className="d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: '#131821', borderBottom: '1px solid #2a2e39' }}>
            <div className="d-flex align-items-center"><span style={{ fontSize: '24px' }}>📝</span><span style={{ color: '#fff', marginLeft: '12px', fontWeight: '500', fontSize: '16px' }}>UserName</span></div>
            <div className="d-flex align-items-center"><span style={{ color: '#848e9c', marginRight: '10px' }}>{userData.username}</span><span className="text-muted">›</span></div>
          </div>
          <div className="d-flex justify-content-between align-items-center p-3"
            onClick={() => { setNewSignature(userData.signature); setShowSignatureModal(true); }}
            style={{ backgroundColor: '#131821', borderBottom: '1px solid #2a2e39', cursor: 'pointer' }}>
            <div className="d-flex align-items-center"><span style={{ fontSize: '24px' }}>✏️</span><span style={{ color: '#fff', marginLeft: '12px', fontWeight: '500', fontSize: '16px' }}>Signature</span></div>
            <div className="d-flex align-items-center"><span style={{ color: '#848e9c', marginRight: '10px' }}>{userData.signature}</span><span className="text-muted">›</span></div>
          </div>
          <div className="d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: '#131821', borderBottom: '1px solid #2a2e39' }}>
            <div className="d-flex align-items-center"><span style={{ fontSize: '24px' }}>🎟️</span><span style={{ color: '#fff', marginLeft: '12px', fontWeight: '500', fontSize: '16px' }}>Invitation Code</span></div>
            <div className="d-flex align-items-center">
              <span style={{ color: '#848e9c', marginRight: '10px' }}>{userData.invitationCode}</span>
              <span className="copy-btn" onClick={() => copyToClipboard(userData.invitationCode)} style={{ fontSize: '12px', padding: '2px 6px' }}>Copy</span>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );

  const renderMyWalletMenu = () => (
    <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <span className="mr-3" style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('profile')}>❮ Back</span>
          <div className="flex-grow-1 text-center font-weight-bold" style={{ fontSize: '18px' }}>My Wallet</div>
          <div style={{ width: '60px' }}></div>
        </div>
        <div className="mt-2">
          <div className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#131821', borderBottom: '1px solid #2a2e39', cursor: 'pointer', padding: '20px', color: 'white', fontSize: '16px' }} onClick={() => setCurrentView('digitalWallet')}>
            <div><span className="mr-3 text-success">₿</span> Digital Wallet</div><span>›</span>
          </div>
          <div className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#131821', borderBottom: '1px solid #2a2e39', cursor: 'pointer', padding: '20px', color: 'white', fontSize: '16px' }} onClick={() => setCurrentView('bankWallet')}>
            <div><span className="mr-3 text-warning">🏦</span> Bank Wallet</div><span>›</span>
          </div>
        </div>
      </Container>
    </div>
  );

  const renderDigitalWalletList = () => (
    <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center justify-content-between p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39' }}>
          <span style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('myWallet')}>❮</span>
          <div className="font-weight-bold" style={{ fontSize: '18px' }}>Digital Wallet</div>
          <span style={{ fontSize: '24px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => openAddWalletModal('digital')}>+</span>
        </div>
        <div className="m-3">
          <h6 style={{ color: '#0ecb81', marginBottom: '15px', fontSize: '14px' }}>₿ My Digital Wallets</h6>
          {digitalWallets.length === 0 && (
            <div className="text-center mt-2 text-muted p-3" style={{ backgroundColor: '#1e2329', borderRadius: '10px' }}>
              No personal digital wallets added yet.
              <div className="mt-2">
                <Button variant="success" size="sm" onClick={() => openAddWalletModal('digital')}>Add Your First Wallet</Button>
              </div>
            </div>
          )}
          {digitalWallets.map(wallet => (
            <Card key={wallet._id || wallet.id} className="mb-3 p-3" style={{ backgroundColor: '#1e2329', border: 'none', color: 'white', borderRadius: '10px' }}>
              <WalletRow label="Currency" value={`${wallet.currency}/${wallet.classification}`} />
              <WalletRow label="Address" value={wallet.address} showCopy />
              <WalletRow label="Comment" value={wallet.comment} />
              <div className="d-flex mt-3" style={{ gap: '15px' }}>
                <Button className="flex-fill" style={{ backgroundColor: '#5c8a00', border: 'none' }} onClick={() => openEditWalletModal('digital', wallet)}>Modify</Button>
                <Button className="flex-fill" style={{ backgroundColor: '#e74c3c', border: 'none' }} onClick={() => initiateDelete('digital', wallet._id || wallet.id)}>Delete</Button>
              </div>
            </Card>
          ))}
        </div>
      </Container>
    </div>
  );

  const renderBankWalletList = () => (
    <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center justify-content-between p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39' }}>
          <span style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('myWallet')}>❮</span>
          <div className="font-weight-bold" style={{ fontSize: '18px' }}>Bank Wallet</div>
          <span style={{ fontSize: '24px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => openAddWalletModal('bank')}>+</span>
        </div>
        <div className="m-3">
          <h6 style={{ color: '#0ecb81', marginBottom: '15px', fontSize: '14px' }}>🏦 My Bank Accounts</h6>
          {bankWallets.length === 0 && (
            <div className="text-center mt-2 text-muted p-3" style={{ backgroundColor: '#1e2329', borderRadius: '10px' }}>
              No personal bank accounts added yet.
              <div className="mt-2">
                <Button variant="success" size="sm" onClick={() => openAddWalletModal('bank')}>Add Your First Bank Account</Button>
              </div>
            </div>
          )}
          {bankWallets.map(wallet => (
            <Card key={wallet._id || wallet.id} className="mb-3 p-3" style={{ backgroundColor: '#1e2329', border: 'none', color: 'white', borderRadius: '10px' }}>
              <WalletRow label="Holder" value={wallet.holderName} showCopy />
              <WalletRow label="Bank Name" value={wallet.bankName} showCopy />
              <WalletRow label="A/C No" value={wallet.accountNo} showCopy />
              <WalletRow label="IFSC Code" value={wallet.ifscCode} showCopy />
              <div className="d-flex mt-3" style={{ gap: '15px' }}>
                <Button className="flex-fill" style={{ backgroundColor: '#5c8a00', border: 'none' }} onClick={() => openEditWalletModal('bank', wallet)}>Modify</Button>
                <Button className="flex-fill" style={{ backgroundColor: '#e74c3c', border: 'none' }} onClick={() => initiateDelete('bank', wallet._id || wallet.id)}>Delete</Button>
              </div>
            </Card>
          ))}
        </div>
      </Container>
    </div>
  );

  const renderSiteNotification = () => (
    <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <span className="mr-3" style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('profile')}>❮ Back</span>
          <div className="flex-grow-1 text-center font-weight-bold" style={{ fontSize: '18px' }}>Site Notification</div>
          <div style={{ width: '60px' }}></div>
        </div>
        <div className="text-center mt-5">
          <div style={{ fontSize: '48px', color: '#848e9c', marginBottom: '20px' }}>🔔</div>
          <h3 style={{ color: '#ffffff', marginBottom: '10px' }}>No data available</h3>
          <p style={{ color: '#848e9c', fontSize: '16px' }}>You don't have any notifications at the moment.</p>
          <Button variant="outline-primary" onClick={() => setCurrentView('profile')} style={{ marginTop: '20px', borderColor: '#0ecb81', color: '#0ecb81' }}>
            Go Back to Profile
          </Button>
        </div>
      </Container>
    </div>
  );

  const renderSiteMessages = () => {
    const unreadMessages = messages.filter(m => !m.isRead);
    const readMessages = messages.filter(m => m.isRead);

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
        <AppHeader />
        <Container className="p-0 mt-3">
          {/* Header */}
          <div className="d-flex align-items-center p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39', color: 'white' }}>
            <span className="mr-3" style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('profile')}>❮ Back</span>
            <div className="flex-grow-1 text-center font-weight-bold" style={{ fontSize: '18px' }}>
              Site Messages
              {unreadCount > 0 && (
                <span style={{
                  marginLeft: '8px',
                  backgroundColor: '#f6465d',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {unreadCount} new
                </span>
              )}
            </div>
            <div style={{ width: '60px' }}></div>
          </div>

          <div className="p-3">
            {messages.length === 0 ? (
              <div className="text-center mt-5">
                <div style={{ fontSize: '48px', color: '#848e9c', marginBottom: '20px' }}>💬</div>
                <h3 style={{ color: '#ffffff', marginBottom: '10px' }}>No messages available</h3>
                <p style={{ color: '#848e9c', fontSize: '16px' }}>You don't have any messages at the moment.</p>
                <Button variant="outline-primary" onClick={() => setCurrentView('profile')} style={{ marginTop: '20px', borderColor: '#0ecb81', color: '#0ecb81' }}>
                  Go Back to Profile
                </Button>
              </div>
            ) : (
              <>
                {unreadMessages.length > 0 && (
                  <div className="mb-3">
                    <h6 style={{ color: '#f6465d', marginBottom: '10px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      🔴 Unread ({unreadMessages.length})
                    </h6>
                    {unreadMessages.map((item) => (
                      <div
                        key={item._id}
                        onClick={() => handleOpenMessage(item)}
                        style={{
                          border: '1px solid #f6465d',
                          padding: '14px 16px',
                          margin: '8px 0',
                          borderRadius: '10px',
                          backgroundColor: '#1e1e2e',
                          cursor: 'pointer',
                          borderLeft: '4px solid #f6465d'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <h6 style={{ color: '#ffffff', fontSize: '15px', margin: 0, fontWeight: 'bold' }}>
                            {item.title || "Notice"}
                            <span style={{ marginLeft: '8px', backgroundColor: '#f6465d', color: 'white', borderRadius: '6px', padding: '1px 6px', fontSize: '10px', verticalAlign: 'middle' }}>NEW</span>
                          </h6>
                          <small style={{ color: '#848e9c', fontSize: '11px', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                            🕐 {formatMessageDate(item)}
                          </small>
                        </div>
                        <p style={{ color: '#b7bdc6', fontSize: '13px', marginTop: '6px', marginBottom: 0 }}>
                          {item.message && item.message.length > 80 ? item.message.substring(0, 80) + '...' : item.message}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {readMessages.length > 0 && (
                  <div>
                    <h6 style={{ color: '#848e9c', marginBottom: '10px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      ✅ Read ({readMessages.length})
                    </h6>
                    {readMessages.map((item) => (
                      <div
                        key={item._id}
                        onClick={() => handleOpenMessage(item)}
                        style={{
                          border: '1px solid #2a2e39',
                          padding: '14px 16px',
                          margin: '8px 0',
                          borderRadius: '10px',
                          backgroundColor: '#1a1a1a',
                          cursor: 'pointer',
                          opacity: 0.8
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <h6 style={{ color: '#b7bdc6', fontSize: '15px', margin: 0 }}>{item.title || "Notice"}</h6>
                          <small style={{ color: '#848e9c', fontSize: '11px', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                            🕐 {formatMessageDate(item)}
                          </small>
                        </div>
                        <p style={{ color: '#6c757d', fontSize: '13px', marginTop: '6px', marginBottom: 0 }}>
                          {item.message && item.message.length > 80 ? item.message.substring(0, 80) + '...' : item.message}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </Container>

        {/* Message Detail Modal */}
        <Modal show={showMessageModal} onHide={() => setShowMessageModal(false)} centered>
          <Modal.Header closeButton style={{ backgroundColor: '#1e2329', borderBottom: '1px solid #2a2e39', color: 'white' }}>
            <Modal.Title style={{ color: 'white', fontSize: '16px' }}>
              {selectedMessage?.title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#131821', color: 'white' }}>
            {selectedMessage && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ backgroundColor: '#1e3a5f', color: '#5ba3f5', borderRadius: '6px', padding: '2px 10px', fontSize: '12px', fontWeight: 'bold' }}>
                    {selectedMessage.messageType === 'admin' ? '📢 Admin Message' : selectedMessage.messageType || 'General'}
                  </span>
                  <small style={{ color: '#848e9c' }}>🕐 Sent: {formatMessageDate(selectedMessage)}</small>
                </div>
                <div style={{ backgroundColor: '#1e2329', borderRadius: '8px', padding: '16px', border: '1px solid #2a2e39' }}>
                  <p style={{ color: '#e0e0e0', whiteSpace: 'pre-wrap', margin: 0, lineHeight: '1.7', fontSize: '15px' }}>
                    {selectedMessage.message || 'No content available'}
                  </p>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#131821', borderTop: '1px solid #2a2e39' }}>
            <Button variant="secondary" onClick={() => setShowMessageModal(false)}>Close</Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  const renderSecuritySettings = () => (
    <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <span className="mr-3" style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('profile')}>❮ Back</span>
          <div className="flex-grow-1 text-center font-weight-bold" style={{ fontSize: '18px' }}>Security Settings</div>
          <div style={{ width: '60px' }}></div>
        </div>
        <div className="text-center mt-5">
          <div style={{ fontSize: '48px', color: '#848e9c', marginBottom: '20px' }}>🔒</div>
          <h3 style={{ color: '#ffffff', marginBottom: '10px' }}>Contact Teacher for Security Changes</h3>
          <p style={{ color: '#848e9c', fontSize: '16px', maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>
            For any changes to your security credentials, password, or account settings,
            please contact your teacher directly for assistance.
          </p>
          <div className="mt-4 p-4" style={{ backgroundColor: '#2a2e39', borderRadius: '10px', maxWidth: '500px', margin: '0 auto' }}>
            <h5 style={{ color: '#0ecb81', marginBottom: '15px' }}>Important Notice:</h5>
            <ul style={{ textAlign: 'left', color: '#b7bdc6', fontSize: '14px', paddingLeft: '20px' }}>
              <li>For password changes, contact teacher</li>
              <li>For fund password reset, contact teacher</li>
              <li>For account security issues, contact teacher</li>
              <li>For two-factor authentication setup, contact teacher</li>
              <li>For any suspicious activity, contact teacher immediately</li>
            </ul>
          </div>
          <Button variant="outline-primary" onClick={() => setCurrentView('profile')} style={{ marginTop: '30px', borderColor: '#0ecb81', color: '#0ecb81', padding: '10px 30px' }}>
            Go Back to Profile
          </Button>
        </div>
      </Container>
    </div>
  );

  const renderPlatformWallet = () => (
    <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <span className="mr-3" style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('profile')}>❮ Back</span>
          <div className="flex-grow-1 text-center font-weight-bold" style={{ fontSize: '18px' }}>Platform Wallet</div>
          <div style={{ width: '60px' }}></div>
        </div>
        <div className="text-center mt-5">
          <div style={{ fontSize: '48px', color: '#848e9c', marginBottom: '20px' }}>📱</div>
          <h3 style={{ color: '#ffffff', marginBottom: '10px' }}>Platform Wallet Information</h3>
          <p style={{ color: '#848e9c', fontSize: '16px', maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>
            Platform wallet features are currently under development.
          </p>
          <Button variant="outline-primary" onClick={() => setCurrentView('profile')} style={{ marginTop: '30px', borderColor: '#0ecb81', color: '#0ecb81', padding: '10px 30px' }}>
            Go Back to Profile
          </Button>
        </div>
      </Container>
    </div>
  );

  const renderAboutCompany = () => (
    <div style={{ minHeight: '100vh', backgroundColor: '#131821', color: 'white' }}>
      <AppHeader />
      <Container className="p-0 mt-3">
        <div className="d-flex align-items-center p-3 shadow-sm mb-3" style={{ backgroundColor: '#0b0e11', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <span className="mr-3" style={{ fontSize: '20px', cursor: 'pointer', color: '#0ecb81' }} onClick={() => setCurrentView('profile')}>❮ Back</span>
          <div className="flex-grow-1 text-center font-weight-bold" style={{ fontSize: '18px' }}>About Company</div>
          <div style={{ width: '60px' }}></div>
        </div>
        <div className="p-4">
          <div className="text-center mb-5">
            <div style={{ fontSize: '60px', color: '#f0b90b', marginBottom: '20px' }}>💎</div>
            <h1 style={{ color: '#ffffff', marginBottom: '10px', fontWeight: 'bold' }}>Super Coin</h1>
            <p style={{ color: '#848e9c', fontSize: '18px' }}>Secure Digital Trading Platform</p>
          </div>
          <div className="mt-4 p-4" style={{ backgroundColor: '#2a2e39', borderRadius: '10px', marginBottom: '20px' }}>
            <h4 style={{ color: '#0ecb81', marginBottom: '15px' }}>Our Mission</h4>
            <p style={{ color: '#b7bdc6', fontSize: '16px', lineHeight: '1.6' }}>
              At Super Coin, we are committed to providing a secure, reliable, and user-friendly
              digital trading platform for cryptocurrency enthusiasts.
            </p>
          </div>
          <div className="p-4" style={{ backgroundColor: '#2a2e39', borderRadius: '10px', marginBottom: '20px' }}>
            <h4 style={{ color: '#0ecb81', marginBottom: '15px' }}>Contact Information</h4>
            <div style={{ color: '#b7bdc6', fontSize: '16px', lineHeight: '1.8' }}>
              <p><strong>📧 Email:</strong> support@supercoin.com</p>
              <p><strong>⏰ Support Hours:</strong> 24/7 Customer Support</p>
            </div>
          </div>
          <Button variant="outline-primary" onClick={() => setCurrentView('profile')} style={{ marginTop: '20px', borderColor: '#0ecb81', color: '#0ecb81', padding: '10px 30px', width: '100%' }}>
            Go Back to Profile
          </Button>
        </div>
      </Container>
    </div>
  );

  // --- MAIN RENDER ---
  return (
    <>
      {currentView === 'profile' && renderMainProfile()}
      {currentView === 'personalInfo' && renderPersonalInfo()}
      {currentView === 'myWallet' && renderMyWalletMenu()}
      {currentView === 'digitalWallet' && renderDigitalWalletList()}
      {currentView === 'bankWallet' && renderBankWalletList()}
      {currentView === 'siteNotification' && renderSiteNotification()}
      {currentView === 'siteMessages' && renderSiteMessages()}
      {currentView === 'securitySettings' && renderSecuritySettings()}
      {currentView === 'platformWallet' && renderPlatformWallet()}
      {currentView === 'aboutCompany' && renderAboutCompany()}

      {/* GENDER MODAL */}
      <Modal show={showGenderModal} onHide={() => setShowGenderModal(false)} centered size="sm" className="dark-modal">
        <Modal.Header closeButton style={{ backgroundColor: '#1e2329', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <Modal.Title style={{ fontSize: '16px' }}>Select Gender</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#131821', padding: '0' }}>
          <ListGroup variant="flush">
            <ListGroup.Item action onClick={() => handleGenderSelect('Male')} style={{ backgroundColor: '#131821', color: 'white', borderBottom: '1px solid #2a2e39' }}>👨 Male</ListGroup.Item>
            <ListGroup.Item action onClick={() => handleGenderSelect('Female')} style={{ backgroundColor: '#131821', color: 'white' }}>👩 Female</ListGroup.Item>
          </ListGroup>
        </Modal.Body>
      </Modal>

      {/* SIGNATURE MODAL */}
      <Modal show={showSignatureModal} onHide={() => setShowSignatureModal(false)} centered className="dark-modal">
        <Modal.Header closeButton style={{ backgroundColor: '#1e2329', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <Modal.Title>Update Signature</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#131821', color: 'white' }}>
          <Form.Group>
            <Form.Label>Your Signature</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              style={{ backgroundColor: 'white', color: 'black' }}
              value={newSignature}
              onChange={(e) => setNewSignature(e.target.value)}
              placeholder="Enter your signature..."
              maxLength={100}
            />
            <Form.Text className="text-muted">{newSignature.length}/100 characters</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#131821', borderTop: '1px solid #2a2e39' }}>
          <Button variant="secondary" onClick={() => setShowSignatureModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleSignatureUpdate}>Save Signature</Button>
        </Modal.Footer>
      </Modal>

      {/* WALLET ADD/EDIT MODAL */}
      <Modal show={showWalletModal} onHide={() => setShowWalletModal(false)} centered className="dark-modal">
        <Modal.Header closeButton style={{ backgroundColor: '#1e2329', borderBottom: '1px solid #2a2e39', color: 'white' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: '600' }}>
            {isEditing ? 'Modify' : 'Add'} {walletModalType === 'digital' ? 'Digital' : 'Bank'} Wallet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#131821', color: 'white', padding: '24px' }}>
          {walletModalType === 'digital' ? (
            <Form>
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>Currency *</Form.Label>
                <Form.Control
                  as="select"
                  style={{ backgroundColor: 'white', color: digitalForm.currency ? '#000000' : '#666666', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  value={digitalForm.currency}
                  onChange={(e) => setDigitalForm({ ...digitalForm, currency: e.target.value, classification: '' })}
                >
                  <option value="">Select Currency</option>
                  {cryptoOptions.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                </Form.Control>
              </Form.Group>
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>Classification *</Form.Label>
                <Form.Control
                  as="select"
                  style={{ backgroundColor: digitalForm.currency ? 'white' : '#f5f5f5', color: digitalForm.classification ? '#000000' : '#666666', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  value={digitalForm.classification}
                  onChange={(e) => setDigitalForm({ ...digitalForm, classification: e.target.value })}
                  disabled={!digitalForm.currency}
                >
                  <option value="">Select Classification</option>
                  {digitalForm.currency && cryptoOptions.find(c => c.code === digitalForm.currency)?.classifications.map(cl => (
                    <option key={cl} value={cl}>{cl}</option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>Address *</Form.Label>
                <Form.Control
                  type="text"
                  style={{ backgroundColor: 'white', color: '#000000', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  placeholder="Enter Wallet Address"
                  value={digitalForm.address}
                  onChange={(e) => setDigitalForm({ ...digitalForm, address: e.target.value })}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>Comment</Form.Label>
                <Form.Control
                  type="text"
                  style={{ backgroundColor: 'white', color: '#000000', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  placeholder="Enter Comment (Optional)"
                  value={digitalForm.comment}
                  onChange={(e) => setDigitalForm({ ...digitalForm, comment: e.target.value })}
                />
              </Form.Group>
            </Form>
          ) : (
            <Form>
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>Holder's Name *</Form.Label>
                <Form.Control
                  type="text"
                  style={{ backgroundColor: 'white', color: '#000000', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  placeholder="Enter Name"
                  value={bankForm.holderName}
                  onChange={(e) => setBankForm({ ...bankForm, holderName: e.target.value })}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>Bank Name *</Form.Label>
                <Form.Control
                  type="text"
                  style={{ backgroundColor: 'white', color: '#000000', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  placeholder="Enter Bank Name"
                  value={bankForm.bankName}
                  onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>Account No *</Form.Label>
                <Form.Control
                  type="text"
                  style={{ backgroundColor: 'white', color: '#000000', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  placeholder="Enter Account Number"
                  value={bankForm.accountNo}
                  onChange={(e) => setBankForm({ ...bankForm, accountNo: e.target.value })}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ color: 'white', fontWeight: '500', display: 'block', marginBottom: '8px', fontSize: '14px' }}>IFSC Code *</Form.Label>
                <Form.Control
                  type="text"
                  style={{ backgroundColor: 'white', color: '#000000', border: '1px solid #ddd', borderRadius: '4px', padding: '10px', fontSize: '14px' }}
                  placeholder="Enter IFSC Code"
                  value={bankForm.ifscCode}
                  onChange={(e) => setBankForm({ ...bankForm, ifscCode: e.target.value })}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#131821', borderTop: '1px solid #2a2e39', padding: '16px 24px' }}>
          <Button variant="secondary" onClick={() => setShowWalletModal(false)} style={{ padding: '8px 20px', fontSize: '14px', fontWeight: '500', border: 'none', borderRadius: '4px' }}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleWalletSubmit} style={{ padding: '8px 20px', fontSize: '14px', fontWeight: '500', border: 'none', borderRadius: '4px', backgroundColor: '#0ecb81', color: 'white' }}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm" className="hint-modal">
        <Modal.Body className="p-4" style={{ backgroundColor: 'white', borderRadius: '5px' }}>
          <h5 className="font-weight-bold mb-3" style={{ color: 'black' }}>Confirm Delete</h5>
          <p className="text-dark mb-4">Are you sure you want to delete this wallet? This action cannot be undone.</p>
          <div className="d-flex justify-content-between" style={{ gap: '10px' }}>
            <Button className="flex-fill" style={{ backgroundColor: '#6c757d', border: 'none' }} onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button className="flex-fill" style={{ backgroundColor: '#e74c3c', border: 'none' }} onClick={confirmDelete}>Delete</Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Profile;
